import { clsx } from '@digital-spiders/misc-utils';
import React from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { CaseStudy } from '../../../templates/CaseStudyPage';
import { CommonModuleProps, ModuleBackgroundColor } from '../../ModulesContent';
import EmbedVideo from '../EmbedVideo';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import * as styles from './BeforeAndAfterModule.module.scss';

export type BeforeAndAfterModuleProps = {
  backgroundColor: ModuleBackgroundColor;
  title: string;
  className?: string;
} & (
  | {
      fillMethod: 'manual';
      beforeAndAfterItems: Array<{
        beforeImage: SanityImageType;
        beforeVideoViews: string;
        afterImage: SanityImageType;
        afterVideoViews: string;
      }>;
      caseStudiesReferences?: never;
    }
  | {
      fillMethod: 'automaticFromCaseStudies';
      beforeAndAfterItems?: never;
      caseStudiesReferences: Array<CaseStudy>;
    }
);

export function getModuleBgColor(props: BeforeAndAfterModuleProps): ModuleBackgroundColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return props.backgroundColor;
}

function BeforeAndAfterModule(
  props: BeforeAndAfterModuleProps & CommonModuleProps,
): React.ReactElement {
  const {
    title,
    fillMethod,
    beforeAndAfterItems,
    caseStudiesReferences,
    className,
    moduleId,
    previousModuleBgColor,
  } = props;

  function renderItemContainer(
    step: 'before' | 'after',
    image: SanityImageType,
    videoUrl?: string,
    videoViews?: string,
    smallerVideoViewsFontSize?: boolean,
  ) {
    const isLandscapeImage = image.asset.width > image.asset.height;

    return (
      <div className={clsx(styles.itemContainer, step === 'before' ? styles.before : styles.after)}>
        <div className={styles.itemTitle}>{step}</div>
        <div className={styles.itemImageContainer}>
          {image && !videoUrl && (
            <Image
              image={image}
              className={clsx(styles.itemImage, isLandscapeImage && styles.contain)}
              height={isLandscapeImage ? undefined : 370}
              width={isLandscapeImage ? undefined : 320}
              cover
            />
          )}
          {videoUrl && <EmbedVideo url={videoUrl} thumbnail={image} className={styles.video} />}
        </div>
        {videoViews && (
          <div
            className={clsx(
              styles.itemSubtitle,
              smallerVideoViewsFontSize && styles.smallerFontSize,
            )}
          >
            {videoViews}
          </div>
        )}
      </div>
    );
  }

  const itemsToShow =
    fillMethod === 'automaticFromCaseStudies'
      ? caseStudiesReferences
          .filter(caseStudy => !!caseStudy.beforeImage && !!caseStudy.afterImage)
          .map(caseStudy => {
            return {
              beforeImage: caseStudy.beforeImage!,
              beforeVideoUrl: caseStudy.beforeVideoUrl,
              beforeVideoViews: caseStudy.beforeVideoViewsNumber,
              afterImage: caseStudy.afterImage!,
              afterVideoUrl: caseStudy.afterVideoUrl,
              afterVideoViews: caseStudy.afterVideoViewsNumber,
            };
          })
      : beforeAndAfterItems;

  return (
    <ModuleLayout
      id={moduleId}
      className={className}
      title={title}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      childrenClassName={styles.content}
    >
      {itemsToShow.map((item, index) => {
        const shouldUseSmallerFontSize =
          (item.beforeVideoViews && item.beforeVideoViews.length > 16) ||
          (item.afterVideoViews && item.afterVideoViews.length > 16);

        return (
          <div
            key={index}
            className={clsx(
              styles.beforeAndAfterContainer,
              shouldUseSmallerFontSize && styles.stretchItems,
            )}
          >
            {renderItemContainer(
              'before',
              item.beforeImage,
              item.beforeVideoUrl,
              item.beforeVideoViews,
              !!shouldUseSmallerFontSize,
            )}
            {renderItemContainer(
              'after',
              item.afterImage,
              item.afterVideoUrl,
              item.afterVideoViews,
              !!shouldUseSmallerFontSize,
            )}
          </div>
        );
      })}
    </ModuleLayout>
  );
}

export default BeforeAndAfterModule;
